import {
  ReactNode,
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";

import moment from "moment";
import { useSearchParams } from "react-router-dom";
import { ListFieldsRecordType, ListFieldsType } from "src/api/config";
import { emailQueueListName } from "src/api/config/email.config";
import {
  notificationsListName,
  sendNotificationsListName,
} from "src/api/config/notifications.config";
import { DrawerComponent, Modal } from "src/components/DialogComponent";
import { EmptyList } from "src/components/EmptyList";
import Loading from "src/components/Loading";
import { NotificationBigIcon } from "src/components/ui-icons/sidebar/NotificationIcon";
import {
  ApproveIcon,
  EyeIcon,
  StarIcon,
} from "src/components/ui-icons/table/notificationStatus";
import SortIcon from "src/components/ui-icons/table/SortIcon";
import Pagination from "src/components/ui/table/Pagination";
import { TableHeaderColumnProps } from "src/components/ui/table/Table";
import TGButton from "src/components/ui/TGButton";
import TGCheckbox from "src/components/ui/TGCheckbox";
import TGTag from "src/components/ui/TGTag";
import {
  StatusType,
  TGUserAvatarsList2,
  UserAvatarItem,
  UserAvatarType,
} from "src/components/ui/TGUserAvatarsList";
import { useAppSelector } from "src/hooks/redux";
import { usePagination } from "src/hooks/usePagination";
import { addAnalysis } from "src/modules/acadia/constants";
import { sendTransformDataByActiveNotify } from "src/modules/acadia/notifications/sagas/notificationsInfoWatcher";
import {
  deleteItemRequest,
  getItemsRequest,
  updateItemRequest,
} from "src/modules/spItems/api";
import { selectGetStatus } from "src/modules/spItems/selectors";
import { selectCurrentUser } from "src/modules/users/selector";
import { useGetQuestionsCount } from "src/pages/DashboardPage/acadia/components/PassingTest/useGetQuestionsCount";
import { TableColumnWithIcon } from "src/pages/DashboardPage/acadia/mock";
import { useStatusContext } from "src/pages/DashboardPage/acadia/notificationPage";
import { LookUpRecord, UserRecord } from "src/types/main";
import { emailAPI } from "src/utils/api.utils";
import { TooltipItem } from "src/utils/Tooltip";
import { nameFromEmail, shortNameFromEmail } from "src/utils/users.utils";
import { chunkArray } from "src/utils/array.utils";
import { notificationStatuses } from "src/api/config/systemSwitch";

const { deleteEmail } = emailAPI();

type TableHeadType =
  | "NotificationType"
  | "Documents"
  | "SendDate"
  | "ReviewDate"
  | "Employees";

type EmployeeType = {
  name: string;
  id: number;
  avatar: string;
  shortName: string;
  listId: number;
  status: StatusType;
};

export type TableBodySendNotificationItem = {
  ID: number;
  NotificationType: LookUpRecord;
  Author: UserRecord;
  AuthorComment: string;
  NotificationID: number;
  SendDate: string;
  Test: number;
  ReviewDate: {
    date: string;
    expired: boolean;
  };
  Documents: Map<
    string,
    {
      Title: string;
      ID: number;
      Code: string;
    }
  >;
  Employees: Map<string, EmployeeType>;
};

const tableHeaderContent: Record<TableHeadType, TableColumnWithIcon> = {
  NotificationType: {
    title: "Тип уведомления",
    sort: true,
    style: { width: "20%" },
  },
  Documents: {
    title: "Документы",
    sort: false,
    style: { width: "20%" },
  },
  Employees: {
    title: "Сотрудники",
    sort: false,
    style: { width: "20%" },
  },
  SendDate: {
    title: "Отправлено",
    sort: true,
    style: { width: "20%" },
  },
  ReviewDate: {
    title: "Срок рассмотрения",
    sort: true,
    style: { width: "20%" },
  },
};

function SendNotificationTable() {
  const sendNotifications = useAppSelector(
    (state) => state.spitems.elements.obj.SendNotifications
  ) as unknown as Record<number, TableBodySendNotificationItem>;
  const [items, setItems] = useState<TableBodySendNotificationItem[]>([]);
  const [page, setPage] = useState(1);
  const [sortedField, setSortedField] = useState<
    keyof TableBodySendNotificationItem | null
  >("SendDate");
  const [sortDirection, setSortDirection] = useState<
    "asc" | "desc" | "default"
  >("asc");
  const limit = 10;

  const handleSort = (field: keyof TableBodySendNotificationItem) => {
    if (sortedField === field) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc"
          ? "desc"
          : prevDirection === "desc"
            ? "default"
            : "asc"
      );
    } else {
      setSortedField(field);
      setSortDirection("asc");
    }
  };

  const sortedTableBodyContent = useMemo(() => {
    if (!sortedField || sortDirection === "default") {
      return items;
    }

    const sortedData = [...items];

    sortedData.sort((a, b) => {
      if (sortedField === "NotificationType") {
        return sortDirection === "asc"
          ? a[sortedField].Title.localeCompare(b[sortedField].Title)
          : b[sortedField].Title.localeCompare(a[sortedField].Title);
      }

      if (sortedField === "ReviewDate") {
        const dateA = moment(a[sortedField]?.date, "DD.MM.YYYY");
        const dateB = moment(b[sortedField]?.date, "DD.MM.YYYY");

        if (!dateA.isValid() || !dateB.isValid()) {
          console.error("Неверная дата", a[sortedField], b[sortedField]);
          return 0;
        }

        return sortDirection === "asc"
          ? dateA.diff(dateB) // если dateA после dateB вернется положительное число
          : dateB.diff(dateA); // для обратного порядка сортировки
      }

      if (sortedField === "SendDate") {
        return sortDirection === "asc"
          ? moment(a[sortedField]).diff(moment(b[sortedField]))
          : moment(b[sortedField]).diff(moment(a[sortedField]));
      }

      const valueA = a[sortedField];
      const valueB = b[sortedField];

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortDirection === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      } else if (typeof valueA === "boolean" && typeof valueB === "boolean") {
        return sortDirection === "asc"
          ? Number(valueA) - Number(valueB)
          : Number(valueB) - Number(valueA);
      }

      return 0;
    });

    return sortedData;
  }, [sortedField, sortDirection, items]) as TableBodySendNotificationItem[];

  const {
    numberOfPages,
    pageItems,
    length: favoriteItemsCount,
  } = usePagination(sortedTableBodyContent, limit, page);

  function changePageHandler(page: number) {
    setPage(page);
  }

  useLayoutEffect(() => {
    if (sendNotifications) {
      if (Object.values(sendNotifications).length > 0) {
        const itemsData = sendTransformDataByActiveNotify(
          Object.values(sendNotifications) as any
        );

        setItems(itemsData);
      } else {
        setItems([]);
      }
    }
  }, [sendNotifications]);

  return (
    <Table
      tableName="Table with get notifications"
      count={favoriteItemsCount}
      numberOfPages={numberOfPages}
      header={tableHeaderContent}
      body={pageItems}
      page={page}
      limit={limit}
      onChangePage={changePageHandler}
      handleSort={handleSort}
      sortDirection={sortDirection}
      sortedField={sortedField}
    />
  );
}

export default SendNotificationTable;

function Table({
  header,
  body,
  page,
  limit,
  count,
  numberOfPages,
  onChangePage,
  tableName,
  handleSort,
  sortDirection,
  sortedField,
}: {
  header: typeof tableHeaderContent;
  body: TableBodySendNotificationItem[];
  limit: number;
  count: number;
  page: number;
  numberOfPages: number;
  onChangePage: (page: number) => void;
  tableName: string;
  handleSort: (f: keyof TableBodySendNotificationItem) => void;
  sortedField: keyof TableBodySendNotificationItem | null;
  sortDirection: "asc" | "desc" | "default";
}) {
  return (
    <FlexRow>
      <FlexCol className="w-full">
        <TableList
          head={header}
          body={body}
          sort={handleSort}
          sortDirection={sortDirection}
          sortedField={sortedField}
          tableName={tableName}
        />
        {count > limit && (
          <Pagination
            numberOfPages={numberOfPages}
            page={page}
            onChange={onChangePage}
          />
        )}
      </FlexCol>
    </FlexRow>
  );
}

function TableList({
  head,
  body,
  sort,
  sortDirection,
  sortedField,
  tableName,
}: {
  head: Omit<typeof tableHeaderContent, "ID">;
  body: TableBodySendNotificationItem[];
  sort: (f: keyof TableBodySendNotificationItem) => void;
  sortedField: keyof TableBodySendNotificationItem | null;
  sortDirection: "asc" | "desc" | "default";
  tableName: string;
}) {
  const [query, setQuery] = useSearchParams();

  const sendNotificationStatus = useAppSelector((state) =>
    selectGetStatus(state, sendNotificationsListName)
  );

  function handleClickNotificationCard(id: number) {
    const newSearchParams = new URLSearchParams(query);

    newSearchParams.set("notificationCard", id.toString());

    setQuery(newSearchParams);
  }

  return (
    <FlexRow className="px-6 py-5 overflow-x-auto bg-white rounded-lg table-custom shadow-custom scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-thumb-[#D1D5DB]">
      <FlexCol className="divide-y min-w-[1252px] grow">
        <FlexRow className="gap-x-4 pb-5">
          {Object.keys(head).map((fieldKey) => {
            const key = fieldKey as TableHeadType;

            return (
              <TableHeaderColumn
                key={fieldKey}
                tableHeaderContent={head[key]}
                sortDirection={sortedField === key ? sortDirection : "default"}
                onClick={() => sort(key)}
              />
            );
          })}
        </FlexRow>
        {sendNotificationStatus !== "SUCCESS" ? (
          <div className="pt-4">
            <Loading />
          </div>
        ) : Array.isArray(body) && body.length > 0 ? (
          body.map((item) => (
            <FlexRow
              className="py-4  hover:bg-[#F3F4F6] hover:bg-opacity-50 rounded-lg  cursor-pointer gap-x-4 -mx-2 px-2"
              key={item.ID}
              onClick={() => handleClickNotificationCard(item.NotificationID)}
            >
              <FlexCol
                className="break-words text-[#637381] justify-center"
                style={{ width: "20%" }}
              >
                <FlexRow className="gap-x-2.5 items-center">
                  <FlexCol>
                    {item.NotificationType.ID ===
                      notificationStatuses.acquainted ||
                      item.NotificationType.ID ===
                      notificationStatuses.acquaintedTest ||
                      item.NotificationType.ID ===
                      notificationStatuses.familiarization ? (
                      <EyeIcon />
                    ) : (
                      ""
                    )}
                    {item.NotificationType.ID ===
                      notificationStatuses.approval ? (
                      <ApproveIcon />
                    ) : (
                      ""
                    )}
                    {item.NotificationType.ID === notificationStatuses.getOWD ||
                      item.NotificationType.ID === notificationStatuses.OWD ? (
                      <StarIcon />
                    ) : (
                      ""
                    )}
                  </FlexCol>
                  <FlexCol>{item.NotificationType.Title}</FlexCol>
                </FlexRow>
              </FlexCol>
              <FlexCol
                className="break-words text-darkblue"
                style={{ width: "20%" }}
              >
                {[...item.Documents.keys()].map((item) => (
                  <FlexRow key={item}>{item}</FlexRow>
                ))}
              </FlexCol>
              <FlexCol
                className="break-words text-[#637381]"
                style={{ width: "20%" }}
              >
                <FlexRow className="gap-x-[5px] items-center">
                  <Employees
                    title={""}
                    employees={sortItems([...item.Employees.values()])}
                  />
                </FlexRow>
              </FlexCol>
              <FlexCol
                className="break-words text-[#637381]"
                style={{ width: "20%" }}
              >
                {item.SendDate}
              </FlexCol>
              <FlexCol
                className={twMerge(
                  "break-words",
                  item.ReviewDate.expired ? "text-red" : ""
                )}
                style={{ width: "20%" }}
              >
                {item.ReviewDate.date}
              </FlexCol>
            </FlexRow>
          ))
        ) : (
          <EmptyList
            icon={<NotificationBigIcon />}
            title="У вас нет актуальных отправленных уведомлений"
          />
        )}
      </FlexCol>
      {query.has("notificationCard") && (
        <SendNotificationDrawer listName={sendNotificationsListName} />
      )}
    </FlexRow>
  );
}

export type RoleNotificationType = "send" | "get" | "all";

type NotificationDrawerProps = {
  listName: typeof sendNotificationsListName | typeof notificationsListName;
  read?: boolean;
  role?: RoleNotificationType;
  item?: ListFieldsType[typeof notificationsListName];
  zIndex?: number;
};

export function SendNotificationDrawer(props: NotificationDrawerProps) {
  const { listName, read = false, role = "all" } = props;
  const [query, setQuery] = useSearchParams();
  const [openNotificationCard, setOpenNotificationCard] = useState(false);

  const [item, setItem] = useState<TableBodySendNotificationItem>();

  const items = useAppSelector((state) => state.spitems.elements.obj[listName]);

  function toggleDrawer(state: boolean) {
    if (!state) {
      query.delete("notificationCard");
      setQuery(query);
    }
    setOpenNotificationCard(state);
  }

  useEffect(() => {
    if (query.has("notificationCard") && items) {
      setOpenNotificationCard(true);

      const itemsModifiedData = sendTransformDataByActiveNotify(
        Object.values(items) as any
      );

      const notificationID = +query.get("notificationCard")!;

      const itemModifiedData = itemsModifiedData.find(
        (item) => item.NotificationID === notificationID
      );

      if (!itemModifiedData) {
        toggleDrawer(false);
      }

      setItem(itemModifiedData);
    } else {
      setOpenNotificationCard(false);
    }
  }, [query, items]);

  if (item === null) {
    return null;
  }

  return (
    <DrawerComponent open={openNotificationCard} toggleDrawer={toggleDrawer}>
      {openNotificationCard && item && (
        <DrawerContent
          role={role}
          read={read}
          item={item as any}
          toggleDrawer={toggleDrawer}
        />
      )}
    </DrawerComponent>
  );
}

function sortItems(items: UserAvatarType[]) {
  return items.toSorted((a: UserAvatarType, b: UserAvatarType) => {
    if (a.status === "success" && b.status === "pending") {
      return -1;
    } else if (a.status === "pending" && b.status === "success") {
      return 1;
    } else {
      return 0;
    }
  });
}

const getAreAllisSuccessed = (employees: EmployeeType[]) =>
  employees.every((cur) => cur.status === "success");

function DrawerContent({
  toggleDrawer,
  item,
  read = false,
  role,
}: {
  toggleDrawer: (t: boolean) => void;
  item: TableBodySendNotificationItem;
  read?: boolean;
  role?: RoleNotificationType;
}) {
  const currentUser = useAppSelector((state) => selectCurrentUser(state));

  const [openDialog, setOpenDialog] = useState(false);
  const [query, setQuery] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const { changeStatus } = useStatusContext();

  ///////////////////////////////////////
  const questionsCount = useGetQuestionsCount(item.Test);
  ///////////////////////////////////////

  function BackButton() {
    query.delete("notificationCard");
    setQuery(query);
    toggleDrawer(false);
  }

  function toggleDialog(state: boolean) {
    setOpenDialog(state);
  }

  async function submitRetract() {
    setLoading(true);
    try {
      if (item) {
        const deleteItems: {
          items: ListFieldsRecordType[typeof notificationsListName][];
        } = await getItemsRequest({
          listName: notificationsListName,
          filters: { NotificationID: item.NotificationID, Hide: false },
        });

        if (!deleteItems.items.length) {
          return new Error(
            "Не найдены документы для скрытия в notificationsListName"
          );
        }

        const isAllUsersNotRead = deleteItems.items.every((item) => !item.Read);

        let deleteItemIds = [];

        if (isAllUsersNotRead) {
          deleteItemIds = deleteItems.items.map((item) => ({ ID: item.ID }));
          const chunkDeleteItemIds = chunkArray(deleteItemIds, 50);

          let promises = chunkDeleteItemIds.map((chunks) =>
            deleteItemRequest({
              listName: notificationsListName,
              ID: 0,
              Ids: chunks,
            })
          );

          await Promise.all(promises);
        } else {
          deleteItemIds = deleteItems.items.map((item) => ({
            ID: item.ID,
            Hide: true,
          }));

          const chunkDeleteItemIds = chunkArray(deleteItemIds, 50);

          let promises = chunkDeleteItemIds.map((chunks) =>
            updateItemRequest({
              listName: notificationsListName,
              body: {},
              bodyArr: chunks,
              update: false,
            })
          );

          await Promise.all(promises);
        }

        const firstDoc = [...item.Documents.values()][0];

        addAnalysis(
          currentUser,
          firstDoc.ID,
          firstDoc.Code,
          "isActive",
          "RetractNotification"
        );

        // Удаление из emailQueue
        if (deleteItemIds.length > 0) {
          const { items } = await getItemsRequest({
            listName: emailQueueListName,
            filters: { NotificationID: item.NotificationID },
          });

          if (items.length > 0) {
            await deleteEmail(items.map((item) => ({ ID: item.ID })));
          }
        }

        setOpenDialog(false);

        query.delete("notificationCard");
        setQuery(query);
      }
    } catch (error) {
      console.error(error, "Ошибка скрытия => submitAudit");
    } finally {
      changeStatus(Date.now());
      setLoading(false);
    }
  }

  function openDocumentCard(id: number) {
    const newSearchParams = new URLSearchParams(query);

    newSearchParams.set("documentCard", id.toString());

    setQuery(newSearchParams);
  }

  if (!item) {
    toggleDrawer(false);
    return null;
  }

  return (
    <FlexCol className="divide-y">
      <header className="flex px-10 pt-10 pb-8 space-x-5 shrink-0">
        <FlexCol>
          <h1 className="text-black">
            {item.NotificationType.ID === notificationStatuses.OWD
              ? "Планирование OWD"
              : "Ознакомление"}
          </h1>
        </FlexCol>
        <FlexCol>
          <TGTag
            className={twMerge("text-black", "translate-y-1")}
            color={item.ReviewDate.expired ? "red" : "gray"}
          >
            до {item.ReviewDate.date}г.
          </TGTag>
        </FlexCol>
      </header>
      <FlexRow className="p-10">
        <FlexCol className="grow">
          <FlexRow
            className={"justify-between items-center font-medium text-black"}
          >
            <FlexCol>
              <div
                className={twMerge(
                  item.Documents.size > 100 ? "text-[#BA0C2F]" : "",
                  "text-[18px] shrink-0"
                )}
              >
                Документы: {"выбрано " + item.Documents.size}
              </div>
            </FlexCol>
          </FlexRow>
          {item.Documents.size > 0 && (
            <FlexRow className="mt-7.5 ">
              <FlexCol className="grow gap-y-2.5">
                {[...item.Documents.values()].map((item) => (
                  <FlexRow
                    className="gap-x-2.5 px-3.5 py-2.5 bg-[#F3F4F6] rounded-lg text-[14px] cursor-pointer"
                    onClick={() => openDocumentCard(item.ID)}
                  >
                    <span>{item.Code}</span>
                    <span className="text-darkblue">{item.Title}</span>
                  </FlexRow>
                ))}
              </FlexCol>
            </FlexRow>
          )}
        </FlexCol>
      </FlexRow>
      <FlexRow className="p-10">
        <Employees
          title={
            item.NotificationType.ID === notificationStatuses.OWD
              ? "Кто проводит OWD"
              : "Сотрудники"
          }
          employees={sortItems([...item.Employees.values()])}
        />
      </FlexRow>
      {/* /////////////////////////////////////////// */}
      {item.NotificationType.ID === notificationStatuses.acquaintedTest && (
        <FlexRow className="p-10 space-x-5 font-medium text-black text-[18px]">
          Тестирование:{" "}
          {!!questionsCount
            ? `требуется, ${questionsCount} вопросов`
            : "не требуется"}{" "}
        </FlexRow>
      )}

      {item.NotificationType.ID === notificationStatuses.familiarization && (
        <FlexRow className="p-10 space-x-5 font-medium text-black text-[18px]">
          Тестирование:{" "}
          {!!questionsCount
            ? `требуется, ${questionsCount} вопросов`
            : "не требуется"}{" "}
        </FlexRow>
      )}
      {/* /////////////////////////////////////////// */}
      {!!item.AuthorComment ? (
        <FlexRow className="p-10 space-x-5 font-medium text-black text-[18px]">
          Комментарий: {item.AuthorComment}
        </FlexRow>
      ) : null}
      <footer className="flex justify-between p-10 shrink-0">
        <TGButton onClick={BackButton}>Закрыть</TGButton>
        <TGButton
          disabled={getAreAllisSuccessed([...item.Employees.values()])}
          onClick={() => setOpenDialog(true)}
        >
          Отозвать уведомление
        </TGButton>
      </footer>
      <Modal
        zIndex={120}
        open={openDialog}
        toggleDialog={toggleDialog}
        modalClassName="min-h-px"
      >
        <header className="px-10 pb-4">
          <h2>Подтвердите действие</h2>
        </header>

        <div className="px-10 py-5 grow">
          <div>Вы уверены, что хотите отозвать отправленное уведомление?</div>
        </div>

        <footer className="flex justify-between px-10 pt-10 mt-4 border-t">
          <TGButton disabled={loading} onClick={() => setOpenDialog(false)}>
            Отмена
          </TGButton>
          <TGButton disabled={loading} color="bluedark" onClick={submitRetract}>
            Да, отозвать
          </TGButton>
        </footer>
      </Modal>
      <Modal
        zIndex={100}
        modalClassName="min-h-0 min-w-min text-center"
        open={loading}
        toggleDialog={() => false}
        hideXMarkIcon={true}
      >
        <header className="px-10 pb-4">
          <h2>Идет сохранение данных</h2>
        </header>

        <div className="justify-center items-center px-10 space-y-5">
          <p className="text-lg">Пожалуйста подождите...</p>
          <Loading />
        </div>
      </Modal>
    </FlexCol>
  );
}

const renderAvatars = (containerWidth: number, employees: UserAvatarType[]) => {
  const countItems = Math.ceil(containerWidth / 26);
  const remainingCount = employees.length - countItems + 1;

  const slidedItems = employees.slice(0, countItems - 1);

  return (
    <TGUserAvatarsList2
      items={slidedItems}
      classImg="w-7.5 h-7.5"
      last={
        countItems >= employees.length ? null : (
          <div
            className={twMerge(
              employees.length > 100 ? "bg-[#BA0C2F]" : "bg-gray-500",
              "flex -translate-x-1 shrink-0 w-7.5 h-7.5 rounded-full ring-white ring-1 items-center justify-center text-white text-[10px]"
            )}
          >
            +{remainingCount >= 999 ? 999 : remainingCount}
          </div>
        )
      }
    />
  );
};

const Employees = memo(
  ({ title, employees }: { title?: string; employees: UserAvatarType[] }) => {
    const [avatar, setAvatar] = useState<ReactNode | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleResize = () => {
        if (containerRef.current) {
          const containerWidth = containerRef.current.clientWidth;
          const node = renderAvatars(containerWidth, employees);
          setAvatar(node);
        }
      };

      if (containerRef.current) {
        handleResize();
      }
    }, [JSON.stringify(employees)]);

    return (
      <FlexCol className="mr-5 grow">
        <FlexRow className="space-x-5 font-medium text-black">
          {title && (
            <FlexCol
              className={twMerge(
                employees.length > 100 ? "text-[#BA0C2F]" : "",
                "text-[18px] shrink-0"
              )}
            >
              {title}: {"выбрано " + employees.length}
            </FlexCol>
          )}
          <FlexCol className="break-words grow">
            <div id="avatar-container" ref={containerRef} className="w-full">
              {employees.length > 0 && avatar}
            </div>
          </FlexCol>
        </FlexRow>
      </FlexCol>
    );
  }
);

export function TableHeaderColumn({
  tableHeaderContent,
  sortDirection,
  onClick,
}: TableHeaderColumnProps) {
  return (
    <FlexCol className="select-none" style={tableHeaderContent.style}>
      <FlexRow className="gap-x-2 items-center">
        {tableHeaderContent.icon && (
          <FlexCol>{tableHeaderContent.icon}</FlexCol>
        )}
        <FlexCol className="font-medium whitespace-pre-line w-fit">
          {tableHeaderContent.title}
        </FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}

// SendOneNotification

export function SendOneNotificationDrawer(props: NotificationDrawerProps) {
  const { listName, read = false, role = "all", item, zIndex } = props;
  const [query, setQuery] = useSearchParams();
  const [openNotificationCard, setOpenNotificationCard] = useState(
    query.has("notificationCard")
  );

  function toggleDrawer(state: boolean) {
    if (!state) {
      query.delete("notificationCard");
      setQuery(query);
    }
    setOpenNotificationCard(state);
  }

  if (item === null) {
    return null;
  }

  return (
    <DrawerComponent
      open={openNotificationCard}
      toggleDrawer={toggleDrawer}
      zIndex={zIndex}
    >
      {openNotificationCard && item && (
        <DrawerOneContent
          role={role}
          read={read}
          item={item}
          toggleDrawer={toggleDrawer}
        />
      )}
    </DrawerComponent>
  );
}

function DrawerOneContent({
  toggleDrawer,
  item,
  read = false,
  role,
}: {
  toggleDrawer: (t: boolean) => void;
  item: ListFieldsType[typeof notificationsListName];
  read?: boolean;
  role?: RoleNotificationType;
}) {
  const [openDialog, setOpenDialog] = useState(false);
  const [query, setQuery] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const { changeStatus } = useStatusContext();

  ///////////////////////////////////////
  const questionsCount = useGetQuestionsCount(item.Test ? item.Test.ID : 0);
  ///////////////////////////////////////

  function BackButton() {
    query.delete("notificationCard");
    setQuery(query);
    toggleDrawer(false);
  }

  function toggleDialog(state: boolean) {
    setOpenDialog(state);
  }

  async function submitRetract() {
    setLoading(true);
    try {
      if (item) {
        const deleteItems: {
          items: ListFieldsRecordType[typeof notificationsListName][];
        } = await getItemsRequest({
          listName: notificationsListName,
          filters: {
            NotificationID: item.NotificationID.ID,
            Hide: false,
          },
        });

        if (!deleteItems.items.length) {
          return new Error(
            "Не найдены документы для скрытия в notificationsListName"
          );
        }

        const sumOfRead = deleteItems.items.filter((item) => item.Read);

        let deleteItemIds = [];

        await deleteItemRequest({
          listName: notificationsListName,
          ID: 0,
          Ids: [{ ID: item.ID }],
        });

        if (
          deleteItems.items.length > 1 &&
          sumOfRead.length + 1 === deleteItems.items.length
        ) {
          deleteItemIds = deleteItems.items
            .filter((el) => el.ID !== item.ID)
            .map((el) => ({ ID: el.ID, Hide: true }));

          await updateItemRequest({
            listName: notificationsListName,
            body: {},
            bodyArr: deleteItemIds,
            update: false,
          });
        }

        if (deleteItems.items.length > 0) {
          const { items } = await getItemsRequest({
            listName: emailQueueListName,
            filters: {
              NotificationID: item.NotificationID.ID,
              Recepients: item.EmployeeEmail,
            },
          });

          if (items.length > 0) {
            await deleteEmail(items.map((item) => ({ ID: item.ID })));
          }
        }

        setOpenDialog(false);

        query.delete("notificationCard");
        setQuery(query);
      }
    } catch (error) {
      console.error(error, "Ошибка скрытия => submitAudit");
    } finally {
      changeStatus(Date.now());
      setLoading(false);
    }
  }

  function openDocumentCard(id: number) {
    const newSearchParams = new URLSearchParams(query);

    newSearchParams.set("documentCard", id.toString());

    setQuery(newSearchParams);
  }

  if (!item) {
    toggleDrawer(false);
    return null;
  }

  return (
    <FlexCol className="divide-y">
      <header className="flex px-10 pt-10 pb-8 space-x-5 shrink-0">
        <FlexCol>
          <h1 className="text-black">
            {item.NotificationType.ID === notificationStatuses.OWD
              ? "Планирование OWD"
              : "Ознакомление"}
          </h1>
        </FlexCol>
        <FlexCol>
          <TGTag
            className={twMerge("text-black", "translate-y-1")}
            color={
              moment(item.ReviewDate).isBefore(new Date()) ? "red" : "gray"
            }
          >
            до {moment(item.ReviewDate).locale("ru").format("DD.MM.YYYY")}г.
          </TGTag>
        </FlexCol>
      </header>
      <FlexRow className="p-10">
        <FlexCol className="grow">
          <h3 className="font-medium text-black mb-7.5">Документ</h3>
          <div>
            <FlexRow>
              <FlexCol className="grow gap-y-2.5">
                <FlexRow
                  className="gap-x-2.5 px-3.5 py-2.5 bg-[#F3F4F6] rounded-lg text-[14px] cursor-pointer"
                  onClick={() => openDocumentCard(item.Document.ID)}
                >
                  <span>{item.DocumentCode}</span>
                  <span className="text-darkblue">{item.Document.Title}</span>
                </FlexRow>
              </FlexCol>
            </FlexRow>
          </div>
        </FlexCol>
      </FlexRow>
      {read && (
        <FlexRow className="p-10 border-t text-[18px] flex space-x-2 text-black font-medium">
          <span>Отправитель:</span>
          <UserAvatarItem
            item={{
              name: nameFromEmail(item.AuthorEmail),
              shortName: shortNameFromEmail(item.AuthorEmail),
              email: item.AuthorEmail,
              id: item.Author.ID,
            }}
            single={true}
          />
        </FlexRow>
      )}

      {item.NotificationType.ID === notificationStatuses.OWD && (
        <>
          <FlexRow className="p-10 border-t text-[18px] flex space-x-2 text-black font-medium">
            <span>Кто проводит OWD:</span>
            <UserAvatarItem
              item={{
                name: nameFromEmail(item.EmployeeEmail),
                shortName: shortNameFromEmail(item.EmployeeEmail),
                email: item.EmployeeEmail,
                id: item.Employee.ID,
              }}
              single={true}
            />
          </FlexRow>

          {read && (
            <FlexRow className="p-10 border-t text-[18px] flex space-x-2 text-black font-medium">
              <span>Кому проводят OWD:</span>
              <UserAvatarItem
                item={{
                  name: nameFromEmail(item.TestedEmployee),
                  shortName: shortNameFromEmail(item.TestedEmployee),
                  email: item.TestedEmployee,
                }}
                single={true}
              />
            </FlexRow>
          )}
        </>
      )}

      {item.NotificationType.ID === notificationStatuses.familiarization && (
        <FlexRow className="p-10 border-t text-[18px] flex justify-between text-black font-medium">
          <FlexCol>
            <FlexRow className="space-x-2">
              <FlexCol>Сотрудник:</FlexCol>
              <FlexCol>
                <UserAvatarItem
                  item={{
                    name: nameFromEmail(item.EmployeeEmail),
                    shortName: shortNameFromEmail(item.EmployeeEmail),
                    email: item.EmployeeEmail,
                    id: item.Employee.ID,
                  }}
                  single={true}
                />
              </FlexCol>
            </FlexRow>
          </FlexCol>
          {read && (
            <FlexCol>
              <FlexRow className="text-[18px] text-black font-medium">
                <TooltipItem
                  disable={true}
                  position="right"
                  tooltipsText="Сначала пройдите тест"
                >
                  <TGCheckbox
                    isChecked={true}
                    onChange={(d) => console.log(d)}
                    disabled={true}
                  >
                    Я прочитал(-а) и подтверждаю факт ознакомления с документом
                  </TGCheckbox>
                </TooltipItem>
              </FlexRow>
            </FlexCol>
          )}
        </FlexRow>
      )}

      {/* /////////////////////////////////////////// */}
      {item.NotificationType.ID === notificationStatuses.familiarization && (
        <FlexRow className="p-10 space-x-5 font-medium text-black text-[18px]">
          Тестирование:{" "}
          {!!questionsCount
            ? `требуется, ${questionsCount} вопросов`
            : "не требуется"}{" "}
        </FlexRow>
      )}
      {/* /////////////////////////////////////////// */}
      {!!item.AuthorComment ? (
        <FlexRow className="p-10 space-x-5 font-medium text-black text-[18px]">
          Комментарий: {item.AuthorComment}
        </FlexRow>
      ) : null}
      <footer className="flex justify-between p-10 shrink-0">
        <TGButton onClick={BackButton}>Закрыть</TGButton>
        {role === "send" && !read && (
          <TGButton onClick={() => setOpenDialog(true)}>
            Отозвать уведомление
          </TGButton>
        )}
      </footer>
      <Modal
        zIndex={120}
        open={openDialog}
        toggleDialog={toggleDialog}
        modalClassName="min-h-px"
      >
        <header className="px-10 pb-4">
          <h2>Подтвердите действие</h2>
        </header>

        <div className="px-10 py-5 grow">
          <div>Вы уверены, что хотите отозвать отправленное уведомление?</div>
        </div>

        <footer className="flex justify-between px-10 pt-10 mt-4 border-t">
          <TGButton disabled={loading} onClick={() => setOpenDialog(false)}>
            Отмена
          </TGButton>
          <TGButton
            disabled={loading}
            color="bluedark"
            onClick={() => submitRetract()}
          >
            Да, отозвать
          </TGButton>
        </footer>
      </Modal>
    </FlexCol>
  );
}
